/*
  Ficha de producto: mantiene sincronizados el selector principal y el de la
  barra fija de compra (floated-form). El theme los trata como dos formularios
  independientes, así que elegir color o medida arriba y pulsar "Add to basket"
  en la barra añadía la variante por defecto.
*/
(() => {
  const isFloated = (el) => !!el && (el.id || '').includes('floated');
  // true mientras este script copia una selección entre selectores: esos
  // cambios no deben recargar la página por cambio de color.
  let syncing = false;

  const selectedValues = (radios) =>
    [...radios.querySelectorAll('.product-form__controls')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value ?? null);

  const applyValues = (target, values) => {
    const fieldsets = [...target.querySelectorAll('.product-form__controls')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
    if (fieldsets.length !== values.length) return;
    if (selectedValues(target).every((value, i) => value === values[i])) return;

    let lastChanged = null;
    fieldsets.forEach((fieldset, i) => {
      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find(
        (el) => el.value === values[i]
      );
      if (input && !input.checked) {
        input.checked = true;
        lastChanged = input;
      }
    });
    if (lastChanged) {
      syncing = true;
      try {
        lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        syncing = false;
      }
    }
  };

  const setCheckedSilently = (radios, values) => {
    const fieldsets = [...radios.querySelectorAll('.product-form__controls')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
    if (fieldsets.length !== values.length) return false;
    fieldsets.forEach((fieldset, i) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input.value === values[i];
      });
    });
    return true;
  };

  const pickers = () => {
    const all = [...document.querySelectorAll('variant-radios')];
    return { main: all.find((el) => !isFloated(el)), floated: all.find(isFloated) };
  };

  // Corazón de wishlist de la ficha: con variantes de color guarda el color
  // elegido (handle?variant=ID de la primera variante disponible de ese color).
  const updateWishlistHeart = (variant, source) => {
    const heart = document.querySelector('.muv-wishlist-toggle--pdp[data-muv-color-index]');
    if (!heart || !variant) return;
    const index = Number(heart.dataset.muvColorIndex);
    const json = source?.querySelector('script[data-all-variants-no-high]');
    let variants = [];
    try { variants = JSON.parse(json?.textContent || '[]'); } catch (e) { variants = []; }
    const sameColor = variants.filter((v) => v.options?.[index] === variant.options?.[index]);
    const pick = sameColor.find((v) => v.available) || sameColor[0] || variant;
    heart.setAttribute('data-wishlist-handle', `${heart.dataset.muvHandle}?variant=${pick.id}`);
    if (window.muvWishlistRefresh) window.muvWishlistRefresh();
    // "Vistos recientemente" recuerda el último color elegido.
    try {
      const key = '__sf_theme_recently';
      const list = JSON.parse(localStorage.getItem(key)) || [];
      const entry = list.find((item) => String(item.productId) === String(heart.dataset.muvProductId));
      if (entry) {
        entry.variantId = String(pick.id);
        localStorage.setItem(key, JSON.stringify(list));
      }
    } catch (e) {}
  };

  // Cambio de COLOR en productos con variantes nativas: se carga la página del
  // color (?variant=), igual que al pulsar un color cuando cada color era un
  // producto. Así el píxel registra la vista de ese color y galería, zoom,
  // barra fija y metadatos salen del color correcto. La medida sigue sin recargar.
  let lastInputKeyboard = false;
  document.addEventListener('keydown', () => { lastInputKeyboard = true; }, true);
  document.addEventListener('pointerdown', () => { lastInputKeyboard = false; }, true);
  document.addEventListener('change', (event) => {
    if (syncing) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const label = input.nextElementSibling;
    if (!label || !label.classList.contains('muv-variant__option--native')) return;
    const radios = input.closest('variant-radios');
    const fieldset = input.closest('.product-form__controls');
    if (!radios || !fieldset) return;

    let variants = [];
    try {
      variants = JSON.parse(radios.querySelector('script[data-all-variants-no-high]')?.textContent || '[]');
    } catch (e) {
      variants = [];
    }
    if (!variants.length) return;

    const position = Number(fieldset.dataset.optionPosition) - 1;
    const wanted = selectedValues(radios);
    wanted[position] = input.value;
    const sameColor = variants.filter((v) => v.options?.[position] === input.value);
    const exact = sameColor.filter((v) => v.options.every((o, i) => o === wanted[i]));
    const target = exact.find((v) => v.available) || sameColor.find((v) => v.available) || exact[0] || sameColor[0];
    if (!target) return;

    event.stopPropagation();
    const url = new URL(radios.dataset.url || location.pathname, location.origin);
    new URLSearchParams(location.search).forEach((value, key) => {
      if (key !== 'variant') url.searchParams.set(key, value);
    });
    url.searchParams.set('variant', target.id);
    radios.classList.add('muv-variant-loading');
    // Solo con teclado: tras recargar, un foco puesto por script enseña el
    // contorno de foco aunque el color se eligiera con el ratón.
    if (lastInputKeyboard) {
      try { sessionStorage.setItem(FOCUS_KEY, input.value); } catch (e) {}
    }
    location.assign(url.toString());
  }, true);

  // Nombre del color junto al título de la opción: al pasar el ratón (o el foco)
  // por un círculo se ve su nombre; al salir vuelve el del color elegido.
  const colorLabelFor = (label) => label.closest('.product-form__controls')?.querySelector('.muv-variant__selected');
  const showColorName = (event) => {
    const label = event.target.closest?.('.muv-variant__option--native');
    if (!label) return;
    const span = colorLabelFor(label);
    const input = document.getElementById(label.htmlFor);
    if (span && input) span.textContent = input.value;
  };
  const restoreColorName = (event) => {
    const label = event.target.closest?.('.muv-variant__option--native');
    if (!label || label.contains(event.relatedTarget)) return;
    const span = colorLabelFor(label);
    if (span) span.textContent = span.dataset.selected || span.textContent;
  };
  document.addEventListener('mouseover', showColorName);
  document.addEventListener('mouseout', restoreColorName);
  document.addEventListener('focusin', showColorName);
  document.addEventListener('focusout', restoreColorName);

  // La barra fija se pinta con el primer color: su nombre se copia del de arriba,
  // que es el de la variante servida.
  const syncColorName = (main, floated) => {
    const src = main?.querySelector('.muv-variant__selected');
    const dst = floated?.querySelector('.muv-variant__selected');
    if (!src || !dst) return;
    const name = src.dataset.selected || src.textContent;
    dst.dataset.selected = name;
    dst.textContent = name;
  };

  // Precio de la barra fija: copia el precio principal cuando el theme lo
  // actualiza al cambiar de medida (el theme no refrescaba el de la barra).
  const syncFloatedPrice = () => {
    const main = document.querySelector('.price-wrapper[id^="price-"] .price');
    const floated = document.querySelector('[id^="FloatedProductInfo-"] .price');
    if (main && floated && floated.innerHTML !== main.innerHTML) floated.innerHTML = main.innerHTML;
  };
  const mainPrice = document.querySelector('.price-wrapper[id^="price-"]');
  if (mainPrice && window.MutationObserver) {
    new MutationObserver(syncFloatedPrice).observe(mainPrice, { childList: true, subtree: true, characterData: true });
  }

  // Al volver con el botón Atrás, el navegador restaura los radios marcados
  // antes de salir, que pueden ser de otro color que el de la página servida.
  // Se vuelven a poner los de la variante de la URL para no añadir al carrito
  // un color distinto del que se ve.
  const alignToServedVariant = () => {
    const { main, floated } = pickers();
    if (!main) return;
    const form = document.querySelector(`#product-form-${main.dataset.section} input[name="id"]`);
    const served = new URLSearchParams(location.search).get('variant') || form?.value;
    if (!served) return;
    let variants = [];
    try {
      variants = JSON.parse(main.querySelector('script[data-all-variants-no-high]')?.textContent || '[]');
    } catch (e) {
      variants = [];
    }
    const variant = variants.find((v) => String(v.id) === String(served));
    if (!variant?.options) return;
    if (selectedValues(main).every((value, i) => value === variant.options[i])) return;
    syncing = true;
    try {
      setCheckedSilently(main, variant.options);
      if (floated) {
        setCheckedSilently(floated, variant.options);
        syncColorName(main, floated);
      }
      document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        if (input.closest('[id^="ProductInfo-"], floated-form')) input.value = String(variant.id);
      });
    } finally {
      syncing = false;
    }
  };
  window.addEventListener('pageshow', alignToServedVariant);

  // Al recargar por cambio de color, devolver el foco al círculo elegido.
  const FOCUS_KEY = 'muv_focus_color';
  try {
    const pending = sessionStorage.getItem(FOCUS_KEY);
    if (pending) {
      sessionStorage.removeItem(FOCUS_KEY);
      const target = [...document.querySelectorAll('.muv-variant__option--native')]
        .map((label) => document.getElementById(label.htmlFor))
        .find((input) => input && input.value === pending);
      if (target) target.focus({ preventScroll: true });
    }
  } catch (e) {}

  document.addEventListener('variant:change', (event) => {
    const source = event.detail?.formElement;
    if (!source) return;
    if (!isFloated(source)) updateWishlistHeart(event.detail?.variant, source);
    const { main, floated } = pickers();
    if (!main || !floated) return;
    floated.dataset.updateUrl = 'false';
    if (isFloated(source)) applyValues(main, selectedValues(floated));
    else applyValues(floated, selectedValues(main));
  });

  // La barra se pinta con un fetch después de cargar la página: cuando aparece,
  // se alinea con lo que ya está elegido arriba (por ejemplo, ?variant= en la URL).
  // Al aparecer la barra se alinea EN SILENCIO (sin disparar 'change'): así el
  // theme no reescribe la URL (se perderían utm/fbclid/gclid de los anuncios) ni
  // hace peticiones extra. Solo se marcan las opciones, la variante del formulario
  // y el precio.
  const floatedForm = document.querySelector('floated-form');
  if (!floatedForm) return;
  const silentAlign = (main, floated) => {
    floated.dataset.updateUrl = 'false';
    const values = selectedValues(main);
    const fieldsets = [...floated.querySelectorAll('.product-form__controls')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
    if (fieldsets.length !== values.length) return;
    fieldsets.forEach((fieldset, i) => {
      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((el) => el.value === values[i]);
      if (input && !input.checked) input.checked = true;
    });
    const mainId = document.querySelector(`#product-form-${main.dataset.section} input[name="id"]`)?.value;
    if (mainId) {
      floatedForm.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        input.value = mainId;
      });
    }
    syncColorName(main, floated);
    syncFloatedPrice();
  };
  const initial = pickers();
  if (initial.main && initial.floated) {
    silentAlign(initial.main, initial.floated);
    return;
  }
  const observer = new MutationObserver(() => {
    const { main, floated } = pickers();
    if (!main || !floated) return;
    observer.disconnect();
    silentAlign(main, floated);
  });
  observer.observe(floatedForm, { childList: true, subtree: true });
})();
